const STORAGE_KEY = 'notifications:seenIds'
const MANUALLY_UNREAD_STORAGE_KEY = 'notifications:manuallyUnreadIds'
// Caps unbounded growth for long-lived accounts; well above realistic list sizes
// (notifications are paginated 30/page) so effectively permanent in practice.
const MAX_STORED_IDS = 500

const readIds = (key: string): string[] => {
    try {
        const raw = localStorage.getItem(key)
        return raw ? JSON.parse(raw) : []
    } catch {
        return []
    }
}

const writeIds = (key: string, ids: string[]) => {
    try {
        localStorage.setItem(key, JSON.stringify(ids.slice(-MAX_STORED_IDS)))
    } catch {
        /* ignore persistence errors (private mode / disabled storage) */
    }
}

const readSeenIds = (): string[] => readIds(STORAGE_KEY)
const writeSeenIds = (ids: string[]) => writeIds(STORAGE_KEY, ids)

/**
 * Tracks notifications the user has opened at least once, independent of the
 * backend `readAt` field — which the "Segna da leggere" toggle resets to null.
 * The unread dot must disappear the first time a notification is opened, and
 * must not come back just because the user re-toggles "Segna da leggere" — but
 * it must come back if the same notification id is genuinely re-delivered
 * with new activity, via `markAsUnseen`.
 */
export const hasBeenSeen = (id: string): boolean => readSeenIds().includes(id)

export const markAsSeen = (id: string) => {
    const ids = readSeenIds()
    if (ids.includes(id)) return
    writeSeenIds([...ids, id])
}

/**
 * Re-surfaces the unread dot for a notification that already exists in the
 * cache and just received new content (see `addNotificationToCache`).
 */
export const markAsUnseen = (id: string) => {
    const ids = readSeenIds()
    if (!ids.includes(id)) return
    writeSeenIds(ids.filter(seenId => seenId !== id))
}

/**
 * Tracks notifications the user has explicitly marked "da leggere" via the
 * CTA. Opening such a notification (including when its dot re-appears after
 * new activity) must clear the dot but must NOT auto-mark it as read again —
 * that would silently override the user's manual choice.
 */
export const wasManuallyMarkedUnread = (id: string): boolean => readIds(MANUALLY_UNREAD_STORAGE_KEY).includes(id)

export const markAsManuallyUnread = (id: string) => {
    const ids = readIds(MANUALLY_UNREAD_STORAGE_KEY)
    if (ids.includes(id)) return
    writeIds(MANUALLY_UNREAD_STORAGE_KEY, [...ids, id])
}

export const clearManuallyUnread = (id: string) => {
    const ids = readIds(MANUALLY_UNREAD_STORAGE_KEY)
    if (!ids.includes(id)) return
    writeIds(
        MANUALLY_UNREAD_STORAGE_KEY,
        ids.filter(unreadId => unreadId !== id)
    )
}

/**
 * Clears every manually-"da leggere" flag at once. Used by "Segna tutte come
 * lette", which — unlike the per-item toggle — has no single id to clear and
 * must reset the highlighted background for all of them in one shot.
 */
export const clearAllManuallyUnread = () => {
    try {
        localStorage.removeItem(MANUALLY_UNREAD_STORAGE_KEY)
    } catch {
        /* ignore persistence errors (private mode / disabled storage) */
    }
}
